import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import ModalReciboVenta, { obtenerReciboVenta } from './ModalReciboVenta';

const BASE = '/api/facturacion/facturas';
const CONSUMIDOR_FINAL = '9999999999999';

const ESTADOS = {
    AUTHORIZED: { texto: 'Autorizada por el SRI', color: '#065f46', fondo: '#d1fae5' },
    QUEUED: { texto: 'En cola, enviándose al SRI', color: '#92400e', fondo: '#fef3c7' },
    PROCESSING: { texto: 'Procesando en el SRI', color: '#92400e', fondo: '#fef3c7' },
    SENT: { texto: 'Enviada al SRI', color: '#92400e', fondo: '#fef3c7' },
    ERROR: { texto: 'Error', color: '#991b1b', fondo: '#fee2e2' },
    REJECTED: { texto: 'Rechazada por el SRI', color: '#991b1b', fondo: '#fee2e2' },
};
export const estadoFactura = (f) => ESTADOS[f?.estado] || { texto: f?.estado_display || f?.estado || '—', color: '#374151', fondo: '#f3f4f6' };

const dinero = (n) => `$${(Math.round((Number(n) || 0) * 100) / 100).toFixed(2)}`;

const tipoDesde = (ident) => {
    const v = (ident || '').trim();
    if (v === CONSUMIDOR_FINAL) return '07';
    if (/^\d{13}$/.test(v) && v.endsWith('001')) return '04';
    if (/^\d{10}$/.test(v)) return '05';
    return '06';
};

const nuevoItem = (iva) => ({ descripcion: '', cantidad: 1, precio: '', descuento: 0, iva });

/**
 * Ventana para emitir una factura electrónica (KoaDoc / SRI).
 * props:
 *  - paciente: objeto paciente (precarga los datos del cliente)
 *  - items: [{descripcion, cantidad, precio, descuento, iva}] precargados desde la venta
 *  - origen / origenId: de dónde viene la venta ('presupuesto', 'registro_mensual', 'paciente', 'manual')
 *  - onEmitida(factura), onClose()
 */
const ModalFactura = ({ paciente = null, items: itemsIniciales = null, origen = 'manual', origenId = '', onEmitida, onClose }) => {
    const [opciones, setOpciones] = useState(null);
    const [consumidorFinal, setConsumidorFinal] = useState(false);
    const [cliente, setCliente] = useState({
        identificacion: paciente?.cedula || '',
        nombre: paciente ? `${paciente.first_name || ''} ${paciente.last_name || ''}`.trim().toUpperCase() : '',
        direccion: paciente?.address || '',
        email: paciente?.email || '',
        telefono: paciente?.phone || '',
    });
    const [pacienteId, setPacienteId] = useState(paciente?.id || null);
    const [busqueda, setBusqueda] = useState('');
    const [resultados, setResultados] = useState([]);
    const [items, setItems] = useState(itemsIniciales?.length ? itemsIniciales : [nuevoItem(15)]);
    const [formaPago, setFormaPago] = useState('01');
    const [errores, setErrores] = useState({});
    const [mensaje, setMensaje] = useState(null);
    const [emitiendo, setEmitiendo] = useState(false);
    const [factura, setFactura] = useState(null);
    const [duplicada, setDuplicada] = useState(null);
    const [busquedaCat, setBusquedaCat] = useState('');
    const [catalogo, setCatalogo] = useState([]);
    const [recibo, setRecibo] = useState(null);
    const [cargandoRecibo, setCargandoRecibo] = useState(false);
    const sondeo = useRef(null);

    useEffect(() => {
        api.get(`${BASE}/opciones/`).then(r => {
            setOpciones(r.data);
            if (!itemsIniciales?.length) setItems([nuevoItem(r.data.iva_defecto)]);
        }).catch(() => setOpciones({ configurado: false, formas_pago: {}, tasas_iva: [0, 5, 12, 14, 15] }));
        return () => clearInterval(sondeo.current);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Buscar paciente para precargar el cliente (cuando la venta no viene con paciente)
    useEffect(() => {
        if (busqueda.trim().length < 2) { setResultados([]); return undefined; }
        const t = setTimeout(() => {
            api.get(`/api/patients/?search=${encodeURIComponent(busqueda.trim())}`)
                .then(r => setResultados((r.data.results || r.data || []).slice(0, 6)))
                .catch(() => setResultados([]));
        }, 300);
        return () => clearTimeout(t);
    }, [busqueda]);

    // Catálogo: servicios e inventario con el precio y el IVA configurados en cada uno.
    useEffect(() => {
        const t = setTimeout(() => {
            api.get(`${BASE}/catalogo/`, { params: { q: busquedaCat.trim() } })
                .then(r => setCatalogo(r.data || []))
                .catch(() => setCatalogo([]));
        }, 250);
        return () => clearTimeout(t);
    }, [busquedaCat]);

    const [autocompletado, setAutocompletado] = useState(false);

    const cargarTratamientos = async (pid) => {
        if (!pid) return;
        try {
            const { data } = await api.get(`${BASE}/tratamientos-paciente/`, { params: { patient_id: pid } });
            if (data?.items?.length > 0) {
                setItems(data.items);
                setAutocompletado(true);
            }
        } catch (e) {
            console.warn('Error cargando tratamientos del paciente:', e);
        }
    };

    useEffect(() => {
        if (paciente?.id && (!itemsIniciales || itemsIniciales.length === 0)) {
            cargarTratamientos(paciente.id);
        }
    }, [paciente]); // eslint-disable-line react-hooks/exhaustive-deps

    const agregarDelCatalogo = (c) => {
        const linea = { codigo: c.codigo, descripcion: c.descripcion, cantidad: 1, precio: Number(c.precio) || '', descuento: 0, iva: c.iva,
            // Productos del inventario: se descuenta el stock al aceptarse la factura.
            ...(c.producto_id ? { producto_id: c.producto_id, stock: Number(c.stock) } : {}) };
        // Si solo hay una fila vacía, se reemplaza en vez de dejarla en blanco.
        setItems(prev => (prev.length === 1 && !prev[0].descripcion && !prev[0].precio ? [linea] : [...prev, linea]));
        setBusquedaCat('');
        setErrores(prev => ({ ...prev, items: undefined }));
    };

    const elegirPaciente = (p) => {
        setPacienteId(p.id);
        setConsumidorFinal(false);
        setCliente({
            identificacion: p.cedula || '', nombre: `${p.first_name || ''} ${p.last_name || ''}`.trim().toUpperCase(),
            direccion: p.address || '', email: p.email || '', telefono: p.phone || ''
        });
        setBusqueda('');
        setResultados([]);
        cargarTratamientos(p.id);
    };

    const cambiarCliente = (campo, valor) => {
        setCliente(prev => ({ ...prev, [campo]: valor }));
        setErrores(prev => ({ ...prev, [campo === 'identificacion' ? 'identificacion' : campo]: undefined }));
    };
    const cambiarItem = (i, campo, valor) => {
        setItems(prev => prev.map((it, k) => (k === i ? { ...it, [campo]: valor } : it)));
        setErrores(prev => ({ ...prev, [`items.${i + 1}`]: undefined, items: undefined }));
    };

    const lineas = items.map(it => {
        const base = Math.max(0, (Number(it.cantidad) || 0) * (Number(it.precio) || 0) - (Number(it.descuento) || 0));
        const iva = Math.round(base * (Number(it.iva) || 0)) / 100;
        return { base: Math.round(base * 100) / 100, iva };
    });
    const subtotal = lineas.reduce((a, l) => a + l.base, 0);
    const ivaTotal = lineas.reduce((a, l) => a + l.iva, 0);
    const total = subtotal + ivaTotal;
    const tope = Number(opciones?.tope_consumidor_final || 50);
    const faltaStock = items.some(it => it.producto_id && Number(it.cantidad) > it.stock);

    const consultarEstado = async (id) => {
        try {
            const { data } = await api.post(`${BASE}/${id}/actualizar/`);
            setFactura(data.factura);
            return data.factura;
        } catch (e) {
            return null;
        }
    };

    const verRecibo = async () => {
        setCargandoRecibo(true);
        try {
            const r = await obtenerReciboVenta(BASE, factura.id);
            if (r) setRecibo(r);
        } catch (e) {
            alert(e.response?.data?.detail || 'No se pudo cargar el recibo.');
        } finally {
            setCargandoRecibo(false);
        }
    };

    // Modo asíncrono de KoaDoc: la factura llega "en cola"; se consulta cada 5 s hasta que el SRI responda (máx. 1 min).
    const seguirEstado = (id) => {
        let intentos = 0;
        clearInterval(sondeo.current);
        sondeo.current = setInterval(async () => {
            intentos += 1;
            const f = await consultarEstado(id);
            if (!f || f.estado === 'AUTHORIZED' || ['ERROR', 'REJECTED', 'NOT_AUTHORIZED'].includes(f.estado) || intentos >= 12) {
                clearInterval(sondeo.current);
            }
        }, 5000);
    };

    const emitir = async (confirmarDuplicado = false) => {
        setEmitiendo(true);
        setErrores({});
        setMensaje(null);
        try {
            const { data } = await api.post(`${BASE}/emitir/`, {
                patient_id: pacienteId,
                origen,
                origen_id: origenId,
                confirmar_duplicado: confirmarDuplicado,
                forma_pago: formaPago,
                cliente: consumidorFinal ? { tipo: '07' } : { ...cliente, tipo: tipoDesde(cliente.identificacion) },
                items,
            });
            setFactura(data.factura);
            setDuplicada(null);
            setMensaje({ ok: true, texto: data.aviso_recibo ? `${data.detail} ${data.aviso_recibo}` : data.detail });
            if (onEmitida) onEmitida(data.factura);
            if (data.factura.estado !== 'AUTHORIZED') seguirEstado(data.factura.id);
        } catch (e) {
            const d = e.response?.data || {};
            if (e.response?.status === 409 && d.duplicado) {
                setDuplicada(d.factura);
            } else {
                setErrores(d.campos || {});
                setMensaje({ ok: false, texto: (d.codigo ? `[${d.codigo}] ` : '') + (d.detail || 'No se pudo emitir la factura.') });
            }
        }
        setEmitiendo(false);
    };

    const err = (campo) => errores[campo] && <div className="mf-err">* {errores[campo]}</div>;
    const est = estadoFactura(factura);

    return (
        <div className="mf-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <style>{`
                .mf-overlay { position: fixed; inset: 0; background: rgba(17,24,39,0.7); z-index: 10040; display: flex; align-items: center; justify-content: center; padding: 16px; }
                .mf-modal { background: #fff; border-radius: 16px; width: 1000px; max-width: 100%; max-height: 94vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px rgba(0,0,0,0.3); }
                .mf-head { background: #111827; color: #fff; padding: 16px 22px; display: flex; align-items: center; justify-content: space-between; }
                .mf-body { padding: 18px 22px; overflow-y: auto; }
                .mf-sec { font-size: 0.75rem; font-weight: 800; letter-spacing: 0.06em; color: #6b7280; text-transform: uppercase; margin: 4px 0 10px; }
                .mf-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px 14px; }
                .mf-f label { display: block; font-size: 0.78rem; font-weight: 700; color: #374151; margin-bottom: 3px; }
                .mf-f input, .mf-f select, .mf-t input, .mf-t select { width: 100%; padding: 8px 10px; border: 1.5px solid #d1d5db; border-radius: 8px; font-size: 0.9rem; box-sizing: border-box; background: #fff; }
                .mf-t select { padding: 8px 6px 8px 8px; min-width: 82px; cursor: pointer; }
                .mf-t input[type="number"] { padding: 8px 6px; }
                .mf-f input.bad, .mf-t input.bad { border-color: #dc2626; background: #fef2f2; }
                .mf-err { color: #dc2626; font-size: 0.78rem; font-weight: 600; margin-top: 2px; }
                .mf-t { width: 100%; border-collapse: collapse; font-size: 0.88rem; min-width: 680px; }
                .mf-t th { text-align: left; font-size: 0.72rem; color: #6b7280; font-weight: 700; padding: 6px 4px; border-bottom: 1px solid #e5e7eb; }
                .mf-t td { padding: 5px 4px; vertical-align: top; }
                .mf-tot { display: flex; justify-content: flex-end; margin-top: 10px; }
                .mf-tot table td { padding: 3px 10px; font-size: 0.92rem; }
                .mf-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: 8px; font-weight: 700; font-size: 0.9rem; cursor: pointer; border: 1.5px solid #111827; background: #111827; color: #fff; }
                .mf-btn.sec { background: #fff; color: #111827; }
                .mf-btn:disabled { opacity: 0.6; cursor: default; }
                .mf-busca { position: relative; }
                .mf-res { position: absolute; left: 0; right: 0; top: 100%; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 20px rgba(0,0,0,0.12); z-index: 5; }
                .mf-res div { padding: 8px 10px; cursor: pointer; font-size: 0.88rem; }
                .mf-res div:hover { background: #f3f4f6; }
                .mf-aviso { padding: 10px 12px; border-radius: 8px; font-size: 0.88rem; font-weight: 600; margin-bottom: 12px; }
                @media (max-width: 760px) { .mf-grid { grid-template-columns: 1fr; } }
            `}</style>
            <div className="mf-modal">
                <div className="mf-head">
                    <div style={{ fontWeight: 800, fontSize: '1.1rem' }}><i className="bi bi-receipt-cutoff me-2"></i>Factura electrónica (SRI)</div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                </div>

                <div className="mf-body">
                    {opciones && !opciones.configurado && (
                        <div className="mf-aviso" style={{ background: '#fef3c7', color: '#92400e' }}>
                            <i className="bi bi-exclamation-triangle me-1"></i>Su usuario no tiene punto de emisión asignado. El administrador debe asignarle la sucursal y el token de KoaDoc en <strong>Usuarios</strong>.
                        </div>
                    )}
                    {opciones?.configurado && !factura && (
                        <div style={{ fontSize: '0.85rem', color: '#374151', marginBottom: 10 }}>
                            <i className="bi bi-shop me-1"></i>Se emitirá desde la sucursal <strong>{opciones.sucursal || 'sin nombre'}</strong>.
                        </div>
                    )}

                    {factura ? (
                        <div>
                            <div className="mf-aviso" style={{ background: est.fondo, color: est.color, fontSize: '1rem' }}>
                                <i className="bi bi-receipt me-2"></i>Factura <strong>{factura.numero || '(sin número)'}</strong> · {est.texto}
                            </div>
                            <table className="mf-t" style={{ maxWidth: 640 }}><tbody>
                                <tr><td><strong>Cliente</strong></td><td>{factura.cliente_nombre} ({factura.cliente_identificacion})</td></tr>
                                <tr><td><strong>Total</strong></td><td>{dinero(factura.total)} (subtotal {dinero(factura.subtotal)} + IVA {dinero(factura.iva)})</td></tr>
                                <tr><td><strong>Clave de acceso</strong></td><td style={{ fontFamily: 'monospace', fontSize: '0.8rem', overflowWrap: 'anywhere' }}>{factura.clave_acceso || '—'}</td></tr>
                                {factura.autorizacion_fecha && <tr><td><strong>Autorizada</strong></td><td>{new Date(factura.autorizacion_fecha).toLocaleString()}</td></tr>}
                                {factura.error_mensaje && <tr><td><strong>Detalle</strong></td><td style={{ color: '#991b1b' }}>{factura.error_mensaje}</td></tr>}
                            </tbody></table>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                                {factura.pdf_url && <a className="mf-btn" href={factura.pdf_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}><i className="bi bi-file-earmark-pdf" style={{ color: '#fff' }}></i><span style={{ color: '#fff' }}>Ver PDF (RIDE)</span></a>}
                                {factura.xml_url && <a className="mf-btn sec" href={factura.xml_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}><i className="bi bi-filetype-xml"></i>XML</a>}
                                <button
                                    type="button"
                                    className="mf-btn"
                                    style={{ background: '#164e9e', borderColor: '#164e9e' }}
                                    onClick={verRecibo}
                                    disabled={cargandoRecibo}
                                >
                                    <i className="bi bi-file-earmark-check me-1"></i>
                                    {cargandoRecibo ? 'Cargando...' : 'Ver / Imprimir Recibo Oficial'}
                                </button>
                                {factura.koadoc_id && factura.estado !== 'AUTHORIZED' && (
                                    <button className="mf-btn sec" onClick={() => consultarEstado(factura.id)}><i className="bi bi-arrow-clockwise"></i>Actualizar estado</button>
                                )}
                                <button className="mf-btn sec" onClick={onClose}>Cerrar</button>
                            </div>
                            {factura.estado !== 'AUTHORIZED' && factura.koadoc_id && (
                                <div style={{ color: '#6b7280', fontSize: '0.82rem', marginTop: 8 }}>
                                    El SRI puede tardar unos segundos o minutos. Se actualiza solo; también puede verla luego en Facturación electrónica.
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {duplicada && (
                                <div className="mf-aviso" style={{ background: '#fef3c7', color: '#92400e' }}>
                                    Esta venta ya tiene la factura <strong>{duplicada.numero || '(en proceso)'}</strong> ({estadoFactura(duplicada).texto}).
                                    <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                                        <button className="mf-btn sec" onClick={() => { setFactura(duplicada); setDuplicada(null); }}>Ver esa factura</button>
                                        <button className="mf-btn" onClick={() => emitir(true)}>Emitir otra de todas formas</button>
                                    </div>
                                </div>
                            )}
                            {mensaje && !mensaje.ok && (
                                <div className="mf-aviso" style={{ background: '#fee2e2', color: '#991b1b' }}><i className="bi bi-x-circle me-1"></i>{mensaje.texto}</div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <div className="mf-sec">Cliente</div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={consumidorFinal} onChange={e => { setConsumidorFinal(e.target.checked); setErrores({}); }} />
                                    Consumidor final (hasta ${tope.toFixed(2)})
                                </label>
                            </div>
                            {errores.tipo && <div className="mf-err" style={{ marginBottom: 8 }}>* {errores.tipo}</div>}

                            {!consumidorFinal && (
                                <>
                                    {!paciente && (
                                        <div className="mf-f mf-busca" style={{ marginBottom: 10 }}>
                                            <label>Buscar paciente (opcional)</label>
                                            <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Nombre o cédula..." />
                                            {resultados.length > 0 && (
                                                <div className="mf-res">
                                                    {resultados.map(p => (
                                                        <div key={p.id} onClick={() => elegirPaciente(p)}>{p.first_name} {p.last_name} · {p.cedula || 'S/C'}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="mf-grid">
                                        <div className="mf-f">
                                            <label>Cédula / RUC / Pasaporte</label>
                                            <input className={errores.identificacion ? 'bad' : ''} value={cliente.identificacion} onChange={e => cambiarCliente('identificacion', e.target.value.trim())} />
                                            {err('identificacion')}
                                            {cliente.identificacion && !errores.identificacion && (
                                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{opciones?.tipos_identificacion?.[tipoDesde(cliente.identificacion)] || ''}</div>
                                            )}
                                        </div>
                                        <div className="mf-f" style={{ gridColumn: 'span 2' }}>
                                            <label>Nombre / Razón social</label>
                                            <input className={errores.nombre ? 'bad' : ''} value={cliente.nombre} onChange={e => cambiarCliente('nombre', e.target.value.toUpperCase())} />
                                            {err('nombre')}
                                        </div>
                                        <div className="mf-f"><label>Correo (recibe XML y PDF)</label><input type="email" value={cliente.email} onChange={e => cambiarCliente('email', e.target.value)} /></div>
                                        <div className="mf-f"><label>Teléfono</label><input value={cliente.telefono} onChange={e => cambiarCliente('telefono', e.target.value)} /></div>
                                        <div className="mf-f"><label>Dirección</label><input value={cliente.direccion} onChange={e => cambiarCliente('direccion', e.target.value)} /></div>
                                    </div>
                                </>
                            )}

                            <div className="mf-sec" style={{ marginTop: 18 }}>Productos y servicios</div>
                            {autocompletado && (
                                <div style={{ fontSize: '0.82rem', color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '7px 12px', borderRadius: 8, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <i className="bi bi-check-circle-fill"></i>
                                    <span>Tratamiento cargado automáticamente desde la ficha del paciente.</span>
                                </div>
                            )}
                            <div className="mf-f mf-busca" style={{ marginBottom: 10 }}>
                                <input value={busquedaCat} onChange={e => setBusquedaCat(e.target.value)}
                                    placeholder="Buscar en servicios e inventario (examen, armazón, lunas...)" />
                                {busquedaCat.trim() && catalogo.length > 0 && (
                                    <div className="mf-res" style={{ maxHeight: 260, overflowY: 'auto' }}>
                                        {catalogo.map((c, k) => (
                                            <div key={k} onClick={() => agregarDelCatalogo(c)} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                                <span>
                                                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: c.tipo === 'Servicio' ? '#1d4ed8' : '#8a7330', marginRight: 6 }}>{c.tipo.toUpperCase()}</span>
                                                    {c.descripcion}
                                                    {c.stock !== undefined && <span style={{ color: '#6b7280', fontSize: '0.75rem' }}> · stock {Number(c.stock)}</span>}
                                                </span>
                                                <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{dinero(c.precio)} · IVA {c.iva}%</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {busquedaCat.trim() && catalogo.length === 0 && (
                                    <div className="mf-res"><div style={{ color: '#6b7280', cursor: 'default' }}>Nada encontrado. Puede escribir el ítem a mano abajo.</div></div>
                                )}
                            </div>
                            {errores.items && <div className="mf-err">* {errores.items}</div>}
                            <div style={{ overflowX: 'auto', width: '100%' }}>
                                <table className="mf-t">
                                    <thead>
                                        <tr>
                                            <th style={{ minWidth: 200 }}>Descripción</th>
                                            <th style={{ width: 70, minWidth: 65 }}>Cant.</th>
                                            <th style={{ width: 110, minWidth: 100 }}>Precio unit.</th>
                                            <th style={{ width: 110, minWidth: 100 }}>Descuento $</th>
                                            <th style={{ width: 95, minWidth: 92 }}>IVA</th>
                                            <th style={{ width: 95, minWidth: 90, textAlign: 'right' }}>Subtotal</th>
                                            <th style={{ width: 38, minWidth: 38 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((it, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <input className={errores[`items.${i + 1}`] ? 'bad' : ''} value={it.descripcion} onChange={e => cambiarItem(i, 'descripcion', e.target.value)} placeholder="Ej: Armazón + lunas monofocales" />
                                                    {errores[`items.${i + 1}`] && <div className="mf-err">* {errores[`items.${i + 1}`]}</div>}
                                                    {it.producto_id && (
                                                        <div style={{ fontSize: '0.74rem', marginTop: 2, color: Number(it.cantidad) > it.stock ? '#dc2626' : '#8a7330', fontWeight: 600 }}>
                                                            <i className="bi bi-box-seam me-1"></i>Inventario · stock {it.stock}
                                                            {Number(it.cantidad) > it.stock ? ' · no alcanza' : ' · se descontará al facturar'}
                                                        </div>
                                                    )}
                                                </td>
                                                <td><input type="number" min="0" step="1" value={it.cantidad} onChange={e => cambiarItem(i, 'cantidad', e.target.value)} /></td>
                                                <td><input type="number" min="0" step="0.01" value={it.precio} onChange={e => cambiarItem(i, 'precio', e.target.value)} /></td>
                                                <td><input type="number" min="0" step="0.01" value={it.descuento} onChange={e => cambiarItem(i, 'descuento', e.target.value)} /></td>
                                                <td>
                                                    <select value={it.iva} onChange={e => cambiarItem(i, 'iva', Number(e.target.value))}>
                                                        {(opciones?.tasas_iva || [0, 5, 12, 14, 15]).map(t => <option key={t} value={t}>{t}%</option>)}
                                                    </select>
                                                </td>
                                                <td style={{ textAlign: 'right', paddingTop: 12 }}>{dinero(lineas[i].base)}</td>
                                                <td>
                                                    {items.length > 1 && (
                                                        <button type="button" onClick={() => setItems(prev => prev.filter((_, k) => k !== i))} title="Quitar de esta factura"
                                                            style={{ border: 'none', background: 'transparent', color: '#6b7280', cursor: 'pointer', paddingTop: 8 }}><i className="bi bi-x-lg"></i></button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button type="button" className="mf-btn sec" style={{ marginTop: 6, padding: '5px 12px', fontSize: '0.82rem' }}
                                onClick={() => setItems(prev => [...prev, nuevoItem(opciones?.iva_defecto ?? 15)])}>
                                <i className="bi bi-plus-lg"></i>Agregar ítem
                            </button>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginTop: 14 }}>
                                <div className="mf-f" style={{ minWidth: 260 }}>
                                    <label>Forma de pago</label>
                                    <select value={formaPago} onChange={e => setFormaPago(e.target.value)}>
                                        {Object.entries(opciones?.formas_pago || { '01': 'Efectivo' }).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                    </select>
                                </div>
                                <div className="mf-tot">
                                    <table><tbody>
                                        <tr><td>Subtotal</td><td style={{ textAlign: 'right' }}>{dinero(subtotal)}</td></tr>
                                        <tr><td>IVA</td><td style={{ textAlign: 'right' }}>{dinero(ivaTotal)}</td></tr>
                                        <tr><td><strong>TOTAL</strong></td><td style={{ textAlign: 'right', fontSize: '1.15rem' }}><strong>{dinero(total)}</strong></td></tr>
                                    </tbody></table>
                                </div>
                            </div>
                            {consumidorFinal && total > tope && (
                                <div className="mf-err" style={{ textAlign: 'right' }}>* A Consumidor Final no se puede facturar más de ${tope.toFixed(2)}: ingrese los datos del cliente.</div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                                <button className="mf-btn sec" onClick={onClose}>Cancelar</button>
                                <button className="mf-btn" onClick={() => emitir(false)} disabled={emitiendo || total <= 0 || faltaStock}
                                    title={faltaStock ? 'Hay productos sin stock suficiente' : ''}>
                                    <i className="bi bi-send-check" style={{ color: '#fff' }}></i>
                                    <span style={{ color: '#fff' }}>{emitiendo ? 'Emitiendo...' : `Emitir factura ${dinero(total)}`}</span>
                                </button>
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '0.78rem', textAlign: 'right', marginTop: 6 }}>
                                Fecha de emisión: hoy. KoaDoc firma la factura, la envía al SRI y se la manda por correo al cliente.
                            </div>
                        </>
                    )}
                </div>
            </div>

            {recibo && (
                <ModalReciboVenta
                    reciboExistente={recibo}
                    onClose={() => setRecibo(null)}
                />
            )}
        </div>
    );
};

export default ModalFactura;
